<script setup>
import { ref, onMounted, onBeforeUnmount, watch, defineProps, defineExpose } from 'vue'
import EditorJS from '@editorjs/editorjs'
import Header from '@editorjs/header'
import List from '@editorjs/list'
import Quote from '@editorjs/quote'
import CodeTool from '@editorjs/code'
import Table from '@editorjs/table'
import Delimiter from '@editorjs/delimiter'
import Embed from '@editorjs/embed'
import Marker from '@editorjs/marker'
import InlineCode from '@editorjs/inline-code'
import Undo from 'editorjs-undo'

const props = defineProps({
  initialData: {
    type: Object,
    default: null
  },
  placeholder: {
    type: String,
    default: 'Type \'/\' for commands...'
  },
  headerPlaceholder: {
    type: String,
    default: 'Title...'
  }
})

const emit = defineEmits(['change'])

const editorHolder = ref(null)
let editor = null
let editorReady = null
let blockObserver = null

const initEditor = async (data = null) => {
  if (editor) {
    // Wait for editor to be ready before destroying
    if (editorReady) {
      try {
        await editorReady
      } catch (e) {
        // Ignore initialization errors
      }
    }
    if (editor && typeof editor.destroy === 'function') {
      await editor.destroy()
    }
    editor = null
    editorReady = null
  }

  // Deep clone data to strip Vue reactivity proxies - EditorJS can't clone Proxy objects
  const plainData = data ? JSON.parse(JSON.stringify(data)) : { blocks: [] }

  const editorConfig = {
    holder: editorHolder.value,
    placeholder: props.placeholder,
    tools: {
      header: {
        class: Header,
        inlineToolbar: true,
        config: {
          placeholder: props.headerPlaceholder,
          levels: [1, 2, 3, 4, 5, 6],
          defaultLevel: 1
        }
      },
      paragraph: {
        inlineToolbar: true,
        config: {
          preserveBlank: true
        }
      },
      list: {
        class: List,
        inlineToolbar: true,
        config: {
          defaultStyle: 'unordered'
        }
      },
      quote: { class: Quote, inlineToolbar: true },
      code: { class: CodeTool },
      table: { class: Table, inlineToolbar: true },
      delimiter: { class: Delimiter },
      embed: { class: Embed },
      marker: { class: Marker, shortcut: 'CMD+SHIFT+M' },
      inlineCode: { class: InlineCode, shortcut: 'CMD+SHIFT+C' },
    },
    data: plainData,
    onReady: () => {
      // Editor.js has no built-in undo/redo — this adds Ctrl/Cmd+Z and Ctrl/Cmd+Y.
      // Guarded so a failure here can't reject isReady and break later setup.
      try { new Undo({ editor }) } catch (err) { console.warn('editorjs-undo init failed', err) }
    },
    onChange: () => {
      emit('change')
    }
  }

  editor = new EditorJS(editorConfig)
  editorReady = editor.isReady
  await editorReady
}

const save = async () => {
  if (!editor) {
    return null
  }
  try {
    const outputData = await editor.save()
    return outputData
  } catch (error) {
    console.error('Editor.js save error:', error)
    return null
  }
}


// Preserve a block's type when copying/cutting a TEXT FRAGMENT. Browsers
// serialize a partial selection inside e.g. an <h2> as bare inline content
// (no <h2> wrapper), so Editor.js pastes it as plain text. Here we rewrite the
// clipboard HTML wrapped in the source block's own tag, so a later paste
// reconstructs the same block type. Whole-block (block-selection) copy is left
// to Editor.js's native, faithful x-editor-js path.
// When a cut empties a block (the whole content was selected), drop the now-empty
// block instead of leaving a blank one behind. Deferred so the cut + Editor.js's
// own mutation handling settle first. Never removes the last remaining block.
function removeBlockIfEmpty(blockEl, isEmpty) {
  const holder = editorHolder.value
  if (!editor || !holder || !blockEl || !isEmpty()) return
  const idx = Array.from(holder.querySelectorAll('.ce-block')).indexOf(blockEl)
  if (idx < 0) return
  Promise.resolve().then(() => {
    try {
      if (editor && editor.blocks.getBlocksCount() > 1) {
        editor.blocks.delete(idx)
        emit('change')
      }
    } catch (_) { /* block already gone */ }
  })
}

// Serialize a whole block to paste-friendly HTML using its own semantic tag, so a
// multi-block copy round-trips (and external paste gets clean markup).
function blockToHtml(blockEl) {
  const content = blockEl.querySelector('.ce-block__content')
  if (!content) return ''
  const ta = blockEl.querySelector('textarea')
  if (ta) { const p = document.createElement('pre'); p.textContent = ta.value; return p.outerHTML }
  const quote = blockEl.querySelector('.cdx-quote__text')
  if (quote) return '<blockquote>' + quote.innerHTML + '</blockquote>'
  const el = content.firstElementChild
  if (!el) return content.innerHTML
  if (el.classList.contains('ce-paragraph')) return '<p>' + el.innerHTML + '</p>'
  const clone = el.cloneNode(true)
  clone.removeAttribute('contenteditable')
  clone.removeAttribute('data-placeholder')
  return clone.outerHTML
}

function handleClipboard(e) {
  const holder = editorHolder.value
  if (!holder) return
  const sel = window.getSelection()

  // --- Multi-block selection (mouse cross-block drag, or keyboard block-select) ---
  // Editor.js's native block-copy only fires for its own keyboard selection flag, so
  // a mouse drag across blocks copies nothing. Detect + handle it ourselves.
  let blocks = Array.from(holder.querySelectorAll('.ce-block--selected'))
  if (blocks.length < 2 && sel && sel.rangeCount && !sel.isCollapsed) {
    const r = sel.getRangeAt(0)
    const elOf = (n) => (n && n.nodeType === Node.TEXT_NODE ? n.parentElement : n)
    const ba = elOf(r.startContainer) && elOf(r.startContainer).closest('.ce-block')
    const bb = elOf(r.endContainer) && elOf(r.endContainer).closest('.ce-block')
    if (ba && bb && ba !== bb) {
      const all = Array.from(holder.querySelectorAll('.ce-block'))
      const i1 = all.indexOf(ba), i2 = all.indexOf(bb)
      if (i1 >= 0 && i2 >= 0) blocks = all.slice(Math.min(i1, i2), Math.max(i1, i2) + 1)
    }
  }
  if (blocks.length >= 2) {
    const all = Array.from(holder.querySelectorAll('.ce-block'))
    const indices = blocks.map((el) => all.indexOf(el)).filter((i) => i >= 0).sort((a, b) => a - b)
    e.clipboardData.setData('text/html', indices.map((i) => blockToHtml(all[i])).join(''))
    e.clipboardData.setData('text/plain', indices.map((i) => all[i].textContent).join('\n\n'))
    e.preventDefault()
    e.stopPropagation() // keep Editor.js's own document handler from double-acting
    if (e.type === 'cut' && editor) {
      Promise.resolve().then(() => {
        for (let k = indices.length - 1; k >= 0; k--) {
          try { editor.blocks.delete(indices[k]) } catch (_) { /* already gone */ }
        }
        try { if (editor.blocks.getBlocksCount() === 0) editor.blocks.insert() } catch (_) {}
        emit('change')
      })
    }
    return
  }

  // Code blocks render their content in a <textarea>, which window.getSelection()
  // can't read. Use the textarea's own selection and wrap in <pre> (code's paste tag).
  const ae = document.activeElement
  if (ae && ae.tagName === 'TEXTAREA' && holder.contains(ae)) {
    const start = ae.selectionStart, end = ae.selectionEnd
    if (start == null || start === end) return
    const text = ae.value.slice(start, end)
    const pre = document.createElement('pre')
    pre.textContent = text
    e.clipboardData.setData('text/html', pre.outerHTML)
    e.clipboardData.setData('text/plain', text)
    e.preventDefault()
    if (e.type === 'cut') {
      const blockEl = ae.closest('.ce-block')
      ae.setRangeText('', start, end, 'end')
      ae.dispatchEvent(new Event('input', { bubbles: true }))
      emit('change') // textarea edits don't trip Editor.js's observer; persist explicitly
      removeBlockIfEmpty(blockEl, () => ae.value.trim() === '')
    }
    return
  }

  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return
  const range = sel.getRangeAt(0)

  let node = range.commonAncestorContainer
  if (node.nodeType === Node.TEXT_NODE) node = node.parentElement
  const content = node && node.closest && node.closest('.ce-block__content')
  // Only act when the whole selection sits inside one block's content.
  if (!content || !content.contains(range.startContainer) || !content.contains(range.endContainer)) return

  // Pick the wrapping tag so paste reconstructs the same block type. Quote renders
  // as nested <div>s, so map it to its paste tag <blockquote>; for header/list the
  // content's own first element (<h{n}>, <ul>/<ol>) is already the right tag.
  let wrapper
  if (node.closest('.cdx-quote')) {
    wrapper = document.createElement('blockquote')
  } else {
    const formatEl = content.firstElementChild
    if (!formatEl) return
    wrapper = formatEl.cloneNode(false)
    wrapper.removeAttribute('contenteditable')
    wrapper.removeAttribute('data-placeholder')
  }
  wrapper.appendChild(range.cloneContents())

  e.clipboardData.setData('text/html', wrapper.outerHTML)
  e.clipboardData.setData('text/plain', sel.toString())
  e.preventDefault()
  if (e.type === 'cut') {
    const blockEl = content.closest('.ce-block')
    // execCommand is observed by Editor.js; if unsupported, delete the range directly.
    if (!document.execCommand('delete')) {
      range.deleteContents()
      emit('change')
    }
    removeBlockIfEmpty(blockEl, () => content.textContent.trim() === '')
  }
}

onMounted(async () => {
  // Attach BEFORE init so copy/cut keep working even if a tool/undo init rejects
  // isReady (which would otherwise abort this function before the listeners bind).
  const holder = editorHolder.value
  if (holder) {
    holder.addEventListener('copy', handleClipboard)
    holder.addEventListener('cut', handleClipboard)
  }
  await initEditor(props.initialData)
})

onBeforeUnmount(async () => {
  const holder = editorHolder.value
  if (holder) {
    holder.removeEventListener('copy', handleClipboard)
    holder.removeEventListener('cut', handleClipboard)
  }

  // Destroy MutationObserver
  if (blockObserver) {
    blockObserver.disconnect()
    blockObserver = null
  }

  // Destroy Editor
  if (editor) {
    if (editorReady) {
      try {
        await editorReady
      } catch (e) {
        // Ignore initialization errors
      }
    }
    if (editor && typeof editor.destroy === 'function') {
      await editor.destroy()
    }
    editor = null
    editorReady = null
  }
})

watch(() => props.initialData, async (newData) => {
  if (newData) {
    await initEditor(newData)
  }
}, { deep: true })

defineExpose({
  save,
  focus
})
</script>

<template>
  <div class="editorjs-wrapper">
    <div ref="editorHolder"></div>
  </div>
</template>

<style scoped>
.editorjs-wrapper {
  color: var(--text-primary);
  font-size: 0.95rem;
  line-height: 1.7;
  /* Small left gutter so the "+" add control (left of the block) stays
     on-screen at full width. */
  padding-left: 1.75rem;
}

/* Full width — drop Editor.js's centered ~650px column */
:deep(.ce-block__content),
:deep(.ce-toolbar__content) {
  max-width: none;
}

:deep(.ce-block) { overflow-wrap: break-word; }
:deep(.ce-paragraph) { overflow-wrap: break-word; }
:deep(.ce-paragraph[data-placeholder]:empty::before),
:deep(.ce-header[data-placeholder]:empty::before) {
  color: var(--text-faint);
  opacity: 1;
}

:deep(.ce-header) { color: var(--text-heading); font-weight: 600; }

/* Single gutter control: the "+" (add block / toolbox). The block-tunes
   handle is removed — adding lives here, converting lives in the inline
   toolbar (select text → "Convert to"), and blocks move via copy/paste. */
:deep(.ce-toolbar__settings-btn) {
  display: none;
}
:deep(.ce-toolbar__plus) {
  color: var(--text-secondary);
  border-radius: var(--radius-sm);
}
:deep(.ce-toolbar__plus:hover) {
  background-color: var(--surface-hover);
  color: var(--text-primary);
}

/* Selection — Editor.js hardcodes near-white blue (#e1f2ff / #d4ecff) which
   reads as white on dark. Recolor block + text selection to the accent. */
:deep(.ce-block--selected .ce-block__content) {
  background: var(--accent-soft-3);
}
:deep(.codex-editor ::selection) {
  background: var(--accent-soft-4);
}

/* Popover (block menu) + inline toolbar — dark to match the app */
:deep(.ce-popover__container),
:deep(.ce-inline-toolbar),
:deep(.ce-conversion-toolbar) {
  background: var(--surface-raised);
  border: 1px solid var(--border);
  color: var(--text-primary);
}
:deep(.ce-popover-item__title),
:deep(.ce-popover-item__icon),
:deep(.ce-inline-tool),
:deep(.ce-conversion-tool__icon),
:deep(.ce-conversion-tool) {
  color: var(--text-primary);
}
:deep(.ce-popover-item:hover),
:deep(.ce-popover-item--focused),
:deep(.ce-inline-tool:hover),
:deep(.ce-conversion-tool:hover) {
  background: var(--surface-hover) !important;
}
:deep(.ce-popover-item__icon),
:deep(.ce-conversion-tool__icon) {
  background: var(--surface-overlay);
  border: 1px solid var(--border-subtle);
}
:deep(.ce-popover__search),
:deep(.cdx-search-field) {
  background: var(--surface-inset);
  border: 1px solid var(--border);
  color: var(--text-primary);
}
:deep(.ce-inline-tool--active) { color: var(--accent-text); }

/* Inline tools */
:deep(.cdx-marker) {
  background: var(--accent-soft-3);
  color: var(--text-primary);
  padding: 0 0.1em;
  border-radius: var(--radius-xs);
}
:deep(.inline-code),
:deep(.cdx-inline-code) {
  background: var(--surface-overlay);
  color: var(--accent-text);
  font-family: 'Menlo', 'Monaco', monospace;
  font-size: 0.85em;
  padding: 0.1em 0.35em;
  border-radius: var(--radius-xs);
}

/* Block tools */
:deep(.cdx-quote),
:deep(.ce-quote) {
  border-left: 3px solid var(--accent);
  background: var(--surface-overlay);
  border-radius: var(--radius-sm);
  padding: 0.5rem 0.9rem;
}
:deep(.ce-code__textarea),
:deep(.cdx-code__textarea) {
  background: var(--surface-sunken);
  color: var(--text-primary);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  font-family: 'Menlo', 'Monaco', monospace;
  font-size: 0.85rem;
}
:deep(.tc-table),
:deep(.tc-row),
:deep(.tc-cell) { border-color: var(--border) !important; }
:deep(.tc-cell) { color: var(--text-primary); }
:deep(.tc-toolbox__toggler),
:deep(.tc-add-row),
:deep(.tc-add-column) { color: var(--text-secondary); }
:deep(.ce-delimiter::before) { color: var(--border-strong); }

:deep(a) { color: var(--accent-text); }
</style>
